import { v4 as uuidv4 } from 'uuid';
import { NetSession } from '../net/session';
import { netService } from './NetService';
import { hubService } from './HubService';
import { saveService } from './SaveService';
import { recoveryService } from './RecoveryService';
import { useGameStore } from '../store/gameSlice';
import { useNetSessionStore } from '../store/netSessionSlice';
import { closeInterface } from './InterfaceService';
import {
  makeEnterDungeon,
  SESSION_CLOSED,
  type EnterDungeonMsg,
} from '../net/sessionEvents';
import type { PeerId } from '../net/transport';
import {
  getClientToken,
  setBoundPlayerId,
  makeReconnectRequest,
  RECONNECT_WINDOW_MS,
  DEV_LOBBY_CODE,
  getCoopResume,
  setCoopResume,
  clearCoopResume,
  clearCoopGround,
  reconnectRegistry,
  RUN_ENDED,
  type ReconnectAcceptMsg,
} from '../net/reconnect';
import { coopDeathHost } from './CoopDeathHost';
import { useReconnectStore } from '../store/reconnectSlice';
import { useSpectatorStore } from '../store/spectatorSlice';
import { resetCoopDeath } from './CoopDeathClient';
import { dungeonService } from './DungeonService';

const CODE_ALPHABET = 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789';

/** How long host()/join() wait for the signaling outcome (lobby created / host
 *  connected) before giving up. Backstop for a stalled negotiation — an invalid
 *  code (in use / no such lobby) fails fast via the signaling error signal. */
const CONNECT_TIMEOUT_MS = 10000;

function generateLobbyCode(): string {
  let code = '';
  for (let i = 0; i < 4; i++) {
    code += CODE_ALPHABET[Math.floor(Math.random() * CODE_ALPHABET.length)];
  }
  return code;
}

class MultiplayerService {
  private offControl: (() => void) | null = null;
  private offHostPeers: (() => void) | null = null;
  private hostClosed = false;
  // Tokens of clients that left while we were down (relayed by signaling at
  // re-host). Held here — NOT in reconnectRegistry, which the Dungeon host effect
  // resets between the run-departures delivery and restoreCoopState.
  private departedTokens = new Set<string>();

  async host(): Promise<string> {
    const code = import.meta.env.DEV ? DEV_LOBBY_CODE : generateLobbyCode();
    const session = new NetSession();
    // The code can collide (always 'AAAA' in dev); the roomId is the true,
    // unique identity a reconnecting client matches against.
    await session.host(code, uuidv4());
    // session.host() resolves once the 'create' is SENT — the signaling server
    // may still reject the code (already in use). Wait for the 'created' ack (or a
    // failure) so we never show "Partie hébergée" for a lobby that was refused.
    await this.awaitSignalingOutcome(session, (s, succeed, fail) => [
      s.onCreated(() => succeed()),
      s.onError(() => fail('host-rejected')),
    ]);
    netService.setSession(session, 'host');
    session.setPhase('hub');
    useNetSessionStore.getState().applySession('host', code);
    const syncCount = () =>
      useNetSessionStore.getState().setPlayerCount(session.peers().length + 1);
    const offJoin = session.onPeerJoin(syncCount);
    const offLeave = session.onPeerLeave(syncCount);
    const offReconnectHub = this.installHostHubReconnect(session);
    this.offHostPeers = () => {
      offJoin();
      offLeave();
      offReconnectHub();
    };
    return code;
  }

  /** Host: answer a reconnect-request that arrives while we're in the hub (the
   *  run ended but the session is still open). The in-dungeon handler
   *  (`Dungeon.tsx`) owns reconnects during a run; this only fires in hub. */
  private installHostHubReconnect(session: NetSession): () => void {
    return session.on('reconnect-request', (from) => {
      if (session.getPhase() === 'hub') {
        session.send(from, 'reliable', RUN_ENDED);
      }
    });
  }

  /** Resume a crashed co-op run as host: re-host the SAVED lobby code so waiting
   *  clients' reconnect loop finds it, then enter the dungeon (the Dungeon host
   *  restore effect rebuilds the run + remote avatars from the save). */
  async resumeHost(code: string, roomId: string): Promise<boolean> {
    this.offHostPeers?.();
    this.offHostPeers = null;
    this.departedTokens.clear();
    const session = new NetSession();
    // Tokens of clients that left while we were down are delivered by signaling
    // right after we re-create the lobby — register before hosting so none slips
    // through.
    const offDepartures = session.onDepartures((tokens) => {
      for (const token of tokens) {
        this.departedTokens.add(token);
        this.dropDepartedIfPresent(token);
      }
    });
    try {
      // Re-host with the SAVED roomId so clients still holding it match on
      // reconnect (a fresh roomId would look like a different room → solo hub).
      await session.host(code, roomId);
    } catch {
      // Signaling unreachable (server down / no network): leave the app on the
      // menu with the save intact so the user can relaunch the server and retry
      // — the caller surfaces the network-error dialog.
      offDepartures();
      session.close();
      return false;
    }
    netService.setSession(session, 'host');
    session.setPhase('dungeon');
    dungeonService.localPlayerId = session.localPeerId();
    useNetSessionStore.getState().applySession('host', code);
    const syncCount = () =>
      useNetSessionStore.getState().setPlayerCount(session.peers().length + 1);
    const offJoin = session.onPeerJoin(syncCount);
    const offLeave = session.onPeerLeave(syncCount);
    const offReconnectHub = this.installHostHubReconnect(session);
    this.offHostPeers = () => {
      offJoin();
      offLeave();
      offDepartures();
      offReconnectHub();
    };
    useGameStore.getState().setGameplay('dungeon');
    return true;
  }

  /** Tokens that left while we were down — consumed by `restoreCoopState` to
   *  skip recreating those avatars. */
  getDepartedTokens(): Set<string> {
    return this.departedTokens;
  }

  /** Drop a player that left while we were down. If its avatar was already
   *  recreated (late delivery), remove it now; otherwise `restoreCoopState`
   *  skips it (the token is in `departedTokens`). Mirrors the P2P `leave-run`
   *  handler. */
  private dropDepartedIfPresent(token: string): void {
    const playerId = reconnectRegistry.playerForToken(token);
    if (!playerId) {
      return;
    }
    reconnectRegistry.forgetPlayer(playerId);
    dungeonService.removeEntity(playerId);
    dungeonService.runPlayerIds.delete(playerId);
    coopDeathHost.handlePlayerLeftRun(playerId);
    // Departure delivered after the run was restored (avatar already recreated):
    // notify the host. The early case (delivered before restore) is handled where
    // restoreCoopState runs. handlePlayerLeftRun keeps the run alive if we remain.
    useNetSessionStore.getState().setNotice('Un joueur a quitté la partie.');
  }

  /** Boot path: the player can't reach signaling to resume their co-op run and
   *  chooses to abandon it — apply the usual defeat recovery (partial loss),
   *  drop the run + co-op records, and enter their solo hub. */
  abandonCoopResumeToSolo(): void {
    recoveryService.applyDefeatRecovery();
    saveService.clearRunSave();
    clearCoopResume();
    clearCoopGround();
    netService.reset();
    useNetSessionStore.getState().clearSession();
    void hubService.enterHub('from-defeat');
  }

  async join(code: string): Promise<void> {
    this.hostClosed = false;
    const session = new NetSession();
    await session.join(code);
    // `join()` only resolves once the signaling 'join' is SENT — the host P2P
    // connection isn't up yet, and the server may still reject the code. Wait for
    // the real host connection (or a definitive failure) so we never present
    // "Connecté" for a dead/invalid lobby; a failure throws → the screen shows an
    // error instead of leaving the player hanging.
    await this.awaitSignalingOutcome(session, (s, succeed, fail) => [
      s.onPeerJoin(() => succeed()),
      s.onError(() => fail('join-rejected')),
      s.onRunClosed(() => fail('run-closed')),
    ]);
    netService.setSession(session, 'client');
    session.setPhase('hub');
    useNetSessionStore.getState().applySession('client', code);
    this.installClientControl(session);
  }

  /** Wait for a definitive signaling outcome before treating a host()/join() as
   *  live. `subscribe` wires the success/failure signals for this role (it returns
   *  the unsubscribe functions); a timeout is the backstop. On failure the session
   *  is closed and the promise rejects — the caller surfaces the error. */
  private awaitSignalingOutcome(
    session: NetSession,
    subscribe: (
      s: NetSession,
      succeed: () => void,
      fail: (reason: string) => void,
    ) => Array<() => void>,
  ): Promise<void> {
    return new Promise<void>((resolve, reject) => {
      let settled = false;
      const finish = (fn: () => void) => {
        if (settled) {
          return;
        }
        settled = true;
        offs.forEach((off) => off());
        window.clearTimeout(timer);
        fn();
      };
      const succeed = () => finish(resolve);
      const fail = (reason: string) =>
        finish(() => {
          session.close();
          reject(new Error(reason));
        });
      const offs = subscribe(session, succeed, fail);
      const timer = window.setTimeout(
        () => fail('connect-timeout'),
        CONNECT_TIMEOUT_MS,
      );
    });
  }

  launchDungeon(dungeonId: string): void {
    const session = netService.session;
    if (!session || !netService.isHost()) {
      return;
    }
    session.setPhase('dungeon');
    // Fresh run: clear any leftover downed/spectator state from a previous run
    // (a player downed at run end keeps it until the next clean launch).
    resetCoopDeath();
    session.broadcast('reliable', makeEnterDungeon(dungeonId));
    saveService.clearRunSave();
    useGameStore.getState().setCurrentDungeonId(dungeonId);
    useGameStore.getState().setGameplay('dungeon');
  }

  leave(): void {
    const session = netService.session;
    if (!session) {
      return;
    }
    if (netService.isHost()) {
      session.broadcast('reliable', SESSION_CLOSED);
      // Mark the run ended on the signaling server too: a client that dropped
      // earlier and reconnects later learns it's over (→ solo hub) instead of
      // waiting for a re-host that won't come. P2P SESSION_CLOSED only reaches
      // peers still connected now.
      const code = useNetSessionStore.getState().code;
      if (code) {
        session.endRun(code);
      }
    }
    const wasClient = netService.isClient();
    this.offControl?.();
    this.offControl = null;
    this.offHostPeers?.();
    this.offHostPeers = null;
    setBoundPlayerId(null);
    clearCoopResume();
    clearCoopGround();
    netService.reset();
    useNetSessionStore.getState().clearSession();
    closeInterface();
    if (wasClient) {
      void hubService.enterHub('default');
    }
  }

  private installClientControl(session: NetSession): void {
    this.offControl?.();
    const offEnter = session.on('enter-dungeon', (_from, m) => {
      const { dungeonId } = m as EnterDungeonMsg;
      // Mark the client session in-dungeon so a host drop here triggers
      // auto-reconnect (onPeerLeave below) rather than an immediate teardown.
      session.setPhase('dungeon');
      // Fresh run on this client too: a downed-at-run-end state must not leak
      // into the next launch (this is the fresh path, not the reconnect one).
      resetCoopDeath();
      // Clear any avatar binding left by a previous reconnect: a fresh launch
      // controls our own peerId and must go through the start barrier ('loading'
      // until run-start) — a stale bound id would skip it and mis-bind the avatar.
      setBoundPlayerId(null);
      const code = useNetSessionStore.getState().code;
      if (code) {
        setCoopResume(code, session.roomId());
      }
      // A hub interface this client left open (inventory, escape menu…) would
      // otherwise render over the dungeon — the host launch yanks us straight in.
      closeInterface();
      useGameStore.getState().setCurrentDungeonId(dungeonId);
      useGameStore.getState().setGameplay('dungeon');
    });
    const offReturn = session.on('return-to-hub', () => {
      clearCoopResume();
      clearCoopGround();
      session.setPhase('hub');
      useNetSessionStore.getState().setNotice('Retour au hub.');
      void hubService.enterHub('default');
    });
    const offClosed = session.on('session-closed', () => {
      this.hostClosed = true;
      this.teardownAsClient('Session terminée — hôte déconnecté.');
    });
    const offLeave = session.onPeerLeave((_id: PeerId) => {
      if (this.hostClosed) {
        return;
      }
      if (session.getPhase() === 'dungeon') {
        void this.reconnect();
      } else {
        this.teardownAsClient('Session terminée — hôte déconnecté.');
      }
    });
    this.offControl = () => {
      offEnter();
      offReturn();
      offClosed();
      offLeave();
    };
  }

  // Safe to call whether or not the session is still active (role may already
  // be 'offline' on reconnect give-up, where netService.reset() ran early).
  private goHome(message: string): void {
    this.offControl?.();
    this.offControl = null;
    netService.reset();
    setBoundPlayerId(null);
    clearCoopResume();
    clearCoopGround();
    useReconnectStore.getState().setReconnecting(false);
    useReconnectStore.getState().setWaiting(false);
    useNetSessionStore.getState().clearSession();
    useNetSessionStore.getState().setNotice(message);
    closeInterface();
    void hubService.enterHub('default');
  }

  private teardownAsClient(message: string): void {
    if (!netService.isClient()) {
      return;
    }
    this.goHome(message);
  }

  /** Reconnect found the run already over but the session still open (host back
   *  in the hub): keep the freshly-joined session and slot into the SHARED hub
   *  as a normal client, rather than hanging on the reconnect overlay. */
  private adoptSessionAsHubClient(session: NetSession, code: string): void {
    netService.setSession(session, 'client');
    session.setPhase('hub');
    setBoundPlayerId(null);
    clearCoopResume();
    clearCoopGround();
    resetCoopDeath();
    useReconnectStore.getState().setReconnecting(false);
    useReconnectStore.getState().setWaiting(false);
    useNetSessionStore.getState().applySession('client', code);
    useNetSessionStore.getState().setNotice('Run terminée — retour au hub.');
    this.installClientControl(session);
    useGameStore.getState().setGameplay('hub');
    void hubService.enterHub('default');
  }

  private async reconnect(): Promise<void> {
    const code = useNetSessionStore.getState().code;
    if (!code) {
      this.goHome('Reconnexion impossible.');
      return;
    }
    // Capture our room's id before tearing the session down — a re-host must
    // present the same roomId or it's a different room (same-code stranger).
    const roomId = netService.session?.roomId() ?? '';
    this.offControl?.();
    this.offControl = null;
    netService.reset();
    useReconnectStore.getState().setReconnecting(true);
    useReconnectStore.getState().setWaiting(true);

    const token = getClientToken();
    // Indefinite wait: the host may relaunch + re-host the same code. The store's
    // `waiting` flag (cleared by quitWaiting) is the single source of truth — read
    // fresh each iteration so it isn't narrowed to a literal across awaits.
    while (useReconnectStore.getState().waiting) {
      const result = await this.tryReconnectOnce(code, token, roomId);
      // Quitter pressed during this attempt: an accept can land concurrently and
      // bind a session via installClientControl. Drop our handler reference and
      // close that session (netService.reset) — quitWaiting() already navigated
      // home. (No offControl?.() here: the session is closed, its handlers inert.)
      if (!useReconnectStore.getState().waiting) {
        this.offControl = null;
        netService.reset();
        return;
      }
      if (result === true) {
        useReconnectStore.getState().setWaiting(false);
        useReconnectStore.getState().setReconnecting(false);
        return;
      }
      if (result === 'ended') {
        // Run over, session still open: adoptSessionAsHubClient already moved us
        // into the shared hub and cleared the overlay.
        return;
      }
      if (result === 'closed') {
        // Host left the game: the run is gone for good → solo hub.
        this.goHome('Run terminée — la partie est fermée.');
        return;
      }
      if (result === 'mismatch') {
        // The code now hosts a DIFFERENT room (a stranger grabbed it): our run
        // is gone → solo hub rather than waiting forever for a room that won't.
        this.goHome('Run introuvable — la partie n’existe plus.');
        return;
      }
      // 'rejected' or recoverable failure → keep waiting (host may be booting).
      await new Promise((r) => window.setTimeout(r, 1000));
    }
  }

  /** User pressed "Quitter" while waiting for a dropped host. Stop the wait loop,
   *  relay the departure through signaling (the host is down, so it can't be told
   *  directly), and forfeit locally. */
  quitWaiting(): void {
    useReconnectStore.getState().setWaiting(false);
    const code = useNetSessionStore.getState().code;
    if (code) {
      this.relayDeparture(code);
    }
    this.goHome('Run quittée.');
  }

  /** Best-effort: tell the signaling server we left run `code` so the host learns
   *  of it when it re-hosts. Lost if signaling is also unreachable — accepted. */
  private relayDeparture(code: string): void {
    const session = new NetSession();
    void session
      .leaveRun(code, getClientToken())
      .catch(() => {})
      .finally(() => {
        // Defer close so the frame flushes before the socket shuts.
        window.setTimeout(() => session.close(), 500);
      });
  }

  /** Boot path: if a co-op dungeon resume record exists (the client reloaded /
   *  crashed mid-run), auto-rejoin and drop straight back into the dungeon.
   *  Reuses the in-session reconnect handshake. On failure, clears the record and
   *  the app stays on the main menu. */
  async resumeCoopRun(): Promise<void> {
    const record = getCoopResume();
    if (!record) {
      return;
    }
    const { code, roomId } = record;
    useReconnectStore.getState().setReconnecting(true);
    const token = getClientToken();
    const deadline = performance.now() + RECONNECT_WINDOW_MS;
    // `reconnecting` is the live cancel flag (cleared by cancelBootResume): read
    // it fresh each turn so the "Annuler" button breaks the loop within one
    // attempt instead of forcing the user to wait out the whole window.
    while (
      performance.now() < deadline &&
      useReconnectStore.getState().reconnecting
    ) {
      const result = await this.tryReconnectOnce(code, token, roomId);
      if (!useReconnectStore.getState().reconnecting) {
        // Cancelled mid-attempt: cancelBootResume already cleared the record +
        // surfaced the notice; the in-flight session closes itself on timeout.
        return;
      }
      if (result === true) {
        // No prior enter-dungeon event on the boot path → enter the scene here
        // (the in-session reconnect() relies on the session already in 'dungeon').
        useGameStore.getState().setGameplay('dungeon');
        useReconnectStore.getState().setReconnecting(false);
        return;
      }
      if (result === 'ended') {
        // Run over, session still open: already slotted into the shared hub.
        return;
      }
      if (result === 'closed') {
        // Host left the game: the run is gone for good → solo hub.
        this.goHome('Run terminée — la partie est fermée.');
        return;
      }
      if (result === 'mismatch') {
        // The code now hosts a DIFFERENT room (stranger grabbed it) → solo hub.
        this.goHome('Run introuvable — la partie n’existe plus.');
        return;
      }
      if (result === 'rejected') {
        break;
      }
      await new Promise((r) => window.setTimeout(r, 800));
    }
    if (!useReconnectStore.getState().reconnecting) {
      return;
    }
    // Give up: stay on the (fresh-boot default) main menu, clear the dead record,
    // and surface a notice. NOT goHome() — that would route to a hub the booting
    // client was never in.
    useReconnectStore.getState().setReconnecting(false);
    clearCoopResume();
    clearCoopGround();
    useNetSessionStore.getState().setNotice('Reconnexion impossible.');
  }

  /** User pressed "Annuler" during the boot reconnect overlay: stop retrying,
   *  drop the dead resume record, and fall back to the main menu. */
  cancelBootResume(): void {
    useReconnectStore.getState().setReconnecting(false);
    clearCoopResume();
    clearCoopGround();
    useNetSessionStore.getState().setNotice('Reconnexion annulée.');
  }

  private async tryReconnectOnce(
    code: string,
    token: string,
    expectedRoomId: string,
  ): Promise<boolean | 'rejected' | 'ended' | 'closed' | 'mismatch'> {
    let session: NetSession;
    try {
      session = new NetSession();
      await session.join(code);
    } catch {
      return false;
    }
    return await new Promise<
      boolean | 'rejected' | 'ended' | 'closed' | 'mismatch'
    >((resolve) => {
      let settled = false;
      const finish = (
        v: boolean | 'rejected' | 'ended' | 'closed' | 'mismatch',
      ) => {
        if (settled) {
          return;
        }
        settled = true;
        offAccept();
        offReject();
        offEnded();
        offClosed();
        offJoin();
        window.clearTimeout(timer);
        resolve(v);
      };
      const offAccept = session.on('reconnect-accept', (_from, m) => {
        const msg = m as ReconnectAcceptMsg;
        netService.setSession(session, 'client');
        // Reclaimed session is in-dungeon — a later host drop must re-trigger
        // auto-reco (this session never went through the enter-dungeon handler).
        session.setPhase('dungeon');
        setBoundPlayerId(msg.playerId);
        dungeonService.localPlayerId = msg.playerId;
        if (msg.downed) {
          useSpectatorStore.getState().setDowned(true);
        }
        useNetSessionStore.getState().applySession('client', code);
        this.installClientControl(session);
        finish(true);
      });
      const offReject = session.on('reconnect-reject', () => {
        session.close();
        finish('rejected');
      });
      // Host is back in the hub: the run is over but the session is still open
      // → join the shared hub instead of waiting to reclaim an avatar.
      const offEnded = session.on('run-ended', () => {
        this.adoptSessionAsHubClient(session, code);
        finish('ended');
      });
      // Signaling reports the run was deliberately closed (host left the game).
      const offClosed = session.onRunClosed(() => {
        session.close();
        finish('closed');
      });
      const timer = window.setTimeout(() => {
        session.close();
        finish(false);
      }, 3000);
      // `join()` resolves once the signaling join is sent — the WebRTC reliable
      // channel to the host is not open yet, so a request sent now is dropped
      // silently. Send it when the host connection opens instead.
      const offJoin = session.onPeerJoin(() => {
        // By now `joined` has set the roomId. If it isn't ours, a stranger
        // grabbed our lobby code — don't reclaim into the wrong room.
        if (expectedRoomId && session.roomId() !== expectedRoomId) {
          session.close();
          finish('mismatch');
          return;
        }
        session.broadcast('reliable', makeReconnectRequest(token));
      });
    });
  }
}

export const multiplayerService = new MultiplayerService();
